import React, { useState, useEffect } from 'react';
import { triggerHaptic } from '../../utils/haptics';

const PullToRefresh = ({ onRefresh, children }) => {
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const pullThreshold = 70; // How far to pull down before triggering

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        setStartY(e.touches[0].clientY);
      } else {
        setStartY(0);
      }
    };

    const handleTouchMove = (e) => {
      if (startY === 0) return;
      const y = e.touches[0].clientY;
      if (y > startY) {
        // Prevent default scrolling when pulling down
        if (e.cancelable) e.preventDefault();
        setCurrentY(y - startY);
      }
    };

    const handleTouchEnd = async () => {
      if (currentY > pullThreshold && !refreshing) {
        setRefreshing(true);
        triggerHaptic.medium(); // Haptic confirmation of refresh
        
        if (onRefresh) {
          await onRefresh();
        } else {
          // Fallback reload if no custom handler provided
          window.location.reload();
        }
        
        setRefreshing(false);
        triggerHaptic.success();
      }
      setStartY(0);
      setCurrentY(0);
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [startY, currentY, refreshing, onRefresh]);

  const pullDistance = Math.min(currentY, pullThreshold + 30);
  
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Refresh Indicator */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `translateY(${refreshing ? '0px' : `-${60 - (pullDistance * 0.8)}px`})`,
        opacity: Math.min(pullDistance / pullThreshold, 1),
        transition: currentY === 0 || refreshing ? 'all 0.3s ease' : 'none',
        zIndex: 50
      }}>
        <div style={{
          background: '#fff',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          {refreshing ? (
            <div className="spinner-border text-danger spinner-border-sm" role="status"></div>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c30000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: `rotate(${pullDistance * 3}deg)` }}>
              <path d="M21 2v6h-6"></path>
              <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8"></path>
            </svg>
          )}
        </div>
      </div>

      {/* Main Content wrapper */}
      <div style={{
        transform: `translateY(${refreshing ? '60px' : `${pullDistance * 0.4}px`})`,
        transition: currentY === 0 || refreshing ? 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)' : 'none',
      }}>
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
